import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import gsap from "gsap";

export const useSmoothScroll = () => {
  const { pathname } = useLocation();
  const isAdmin = pathname.startsWith("/admin");
  const scrollState = useRef({
    currentY: 0,
    targetY: 0,
  });

  useEffect(() => {
    if (isAdmin) return;

    // Force scrollBehavior to auto to prevent double-smoothing conflict with JS animations
    document.documentElement.style.scrollBehavior = "auto";
    
    // Initialize scroll state to current scroll position
    scrollState.current.currentY = window.scrollY;
    scrollState.current.targetY = window.scrollY;

    const handleWheel = (e: WheelEvent) => {
      if (e.ctrlKey || e.metaKey || e.shiftKey) return;
      e.preventDefault();

      const absDeltaY = Math.abs(e.deltaY);
      const isTrackpad = absDeltaY < 30 || !Number.isInteger(e.deltaY);

      // Tighten the trackpad scroll speed significantly as requested by the user
      // Trackpads: multiplier 0.25, Mice: multiplier 1.2
      const multiplier = isTrackpad ? 0.25 : 1.2;
      const targetDelta = e.deltaY * multiplier;

      const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
      const currentScroll = window.scrollY;

      // Sync if scrolled externally (scrollbar, page up/down keys)
      if (Math.abs(scrollState.current.currentY - currentScroll) > 10) {
        scrollState.current.currentY = currentScroll;
        scrollState.current.targetY = currentScroll;
      }

      scrollState.current.targetY = Math.max(0, Math.min(maxScroll, scrollState.current.targetY + targetDelta));

      // Tween currentY to targetY using GSAP
      gsap.to(scrollState.current, {
        currentY: scrollState.current.targetY,
        duration: isTrackpad ? 0.15 : 0.45,
        ease: "power2.out",
        overwrite: "auto",
        onUpdate: () => {
          window.scrollTo(0, scrollState.current.currentY);
        },
      });
    };

    window.addEventListener("wheel", handleWheel, { passive: false });

    return () => {
      window.removeEventListener("wheel", handleWheel);
      gsap.killTweensOf(scrollState.current);
      document.documentElement.style.scrollBehavior = "";
    };
  }, [isAdmin]);

  // Sync scroll position on route change immediately
  useEffect(() => {
    if (isAdmin) return;
    gsap.killTweensOf(scrollState.current);
    document.documentElement.style.scrollBehavior = "auto";
    window.scrollTo(0, 0);
    scrollState.current.currentY = 0;
    scrollState.current.targetY = 0;
  }, [pathname, isAdmin]);
};

export default useSmoothScroll;
